import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, EmptyState, Select, Skeleton } from '../ui';
import { MasteryMark } from '../etch/MasteryMark';
import { MasteryTimeline } from '../etch/MasteryTimeline';
import { MASTERY_LEVELS, missionErrorKeys, useMissions } from '../../context/MissionContext';

/**
 * Mastery overview (spec §9.3, §10.3 "Kompetenzverlauf").
 *
 * Three readings, in order of how much they claim:
 *   1. where each mission stands and *why* it stands there,
 *   2. how well confidence matched correctness (calibration),
 *   3. what activity happened over time — labelled as activity, never as
 *      competence (§3.3 principle 4).
 */

const RANGES = [7, 30, 90];

function calibrationVerdictFromBias(bias) {
  if (bias === null || bias === undefined) return null;
  if (bias > 0.1) return 'overconfident';
  if (bias < -0.1) return 'underconfident';
  return 'good';
}

function formatDate(value, language) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(language);
}

export function MasteryOverview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { fetchMastery, offline } = useMissions();

  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (range, signal) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchMastery(range, { signal });
        setData(result);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [fetchMastery]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(days, controller.signal);
    return () => controller.abort();
  }, [days, load]);

  const summary = data?.summary ?? null;
  const levels = data?.levels ?? [];
  const calibration = data?.calibration ?? null;
  const verdict = calibrationVerdictFromBias(calibration?.bias);
  const isSessionProblem =
    error && ['session_expired', 'permission_denied', 'app_not_licensed', 'tenant_suspended'].includes(error.code);

  return (
    <div className="etch-view">
      <div className="etch-column" data-width="wide">
        <header className="etch-page-header">
          <div className="etch-page-header__title">
            <span className="etch-eyebrow">{t('nav.missions')}</span>
            <h1>{t('mastery.timeline')}</h1>
            <p>{t('mastery.overviewSubtitle')}</p>
          </div>
          <div className="etch-page-header__actions">
            <Select
              label={t('mastery.rangeLabel')}
              hideLabel
              value={String(days)}
              onChange={(event) => setDays(Number(event.target.value))}
              options={RANGES.map((range) => ({
                value: String(range),
                label: t('mastery.rangeDays', { count: range }),
              }))}
            />
            <Button variant="ghost" size="compact" onClick={() => navigate('/missions')}>
              {t('missions.backToCenter')}
            </Button>
          </div>
        </header>

        {offline && (
          <Alert tone="warning" title={t('states.offline')}>
            {t('missions.offlineCenterBody')}
          </Alert>
        )}

        {error && (
          <Alert tone={isSessionProblem ? 'warning' : 'danger'} title={t(missionErrorKeys(error))}>
            <p>{t('mastery.loadFailedBody')}</p>
            {!isSessionProblem && (
              <Button variant="secondary" size="compact" onClick={() => load(days)}>
                {t('states.retry')}
              </Button>
            )}
          </Alert>
        )}

        {loading && (
          <div className="etch-stack" data-gap="lg">
            <p className="etch-visually-hidden" role="status">
              {t('states.loading')}
            </p>
            <Skeleton height="6rem" />
            <Skeleton height="12rem" />
          </div>
        )}

        {!loading && !error && summary && summary.total === 0 && (
          <EmptyState title={t('mastery.emptyTitle')}>{t('mastery.emptyBody')}</EmptyState>
        )}

        {!loading && !error && summary && summary.total > 0 && (
          <div className="etch-stack" data-gap="lg">
            <section className="etch-mastery-summary" aria-labelledby="etch-mastery-summary-heading">
              <h2 id="etch-mastery-summary-heading">{t('mastery.summaryTitle')}</h2>
              <ul className="etch-mastery-summary__levels">
                {MASTERY_LEVELS.map((level) => (
                  <li key={level} className="etch-mastery-summary__level" data-level={level}>
                    <MasteryMark level={level} reasons={[]} size="compact" />
                    <span className="etch-mastery-summary__count">{summary.counts?.[level] ?? 0}</span>
                  </li>
                ))}
              </ul>
              <p className="etch-meta">{t('mastery.summaryHint')}</p>
            </section>

            <section className="etch-calibration" data-verdict={verdict ?? 'unknown'} aria-labelledby="etch-calibration-heading">
              <h2 id="etch-calibration-heading">{t('mastery.calibration')}</h2>
              <p>
                {verdict === 'overconfident' && t('mastery.calibrationOverconfidentText')}
                {verdict === 'underconfident' && t('mastery.calibrationUnderconfidentText')}
                {verdict === 'good' && t('mastery.calibrationGoodText')}
                {!verdict && t('mastery.calibrationUnknownText')}
              </p>
              <p className="etch-meta">
                {t('mastery.calibrationSamples', { count: calibration?.samples ?? 0 })}
              </p>
            </section>

            <section aria-labelledby="etch-mastery-timeline-heading">
              <h2 id="etch-mastery-timeline-heading">{t('mastery.activityTitle')}</h2>
              <MasteryTimeline points={data.timeline} />
            </section>

            <section aria-labelledby="etch-mastery-levels-heading">
              <h2 id="etch-mastery-levels-heading">{t('mastery.perMissionTitle')}</h2>
              <ul className="etch-mastery-list">
                {levels.map((entry) => {
                  const last = formatDate(entry.lastAttemptAt, i18n.language);
                  const next = formatDate(entry.nextRevisitAt, i18n.language);
                  return (
                    <li key={entry.missionId} className="etch-mastery-list__item" data-level={entry.level}>
                      <div className="etch-mastery-list__head">
                        <h3 className="etch-mastery-list__title">{entry.title}</h3>
                        {entry.topic && <Badge>{entry.topic}</Badge>}
                      </div>
                      <MasteryMark level={entry.level} reasons={entry.reasons ?? []} showWhy />
                      <p className="etch-meta">
                        {last ? t('mastery.lastAttempt', { date: last }) : t('mastery.neverAttempted')}
                        {next ? ` · ${t('mastery.nextRevisit', { date: next })}` : ''}
                      </p>
                      <Button
                        variant="ghost"
                        size="compact"
                        onClick={() => navigate(`/missions/run/${entry.missionId}`)}
                        aria-label={t('missions.startNamed', { title: entry.title })}
                      >
                        {t('missions.start')}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default MasteryOverview;
